import React, { Component } from 'react';
import { flowRight as compose } from 'lodash';
import { withQuery, withMutation } from '../../../hoc/withGraphql';
import getAllRefunds from './getAllRefunds.graphql';
import getAllDisputes from './getAllDisputes.graphql';
import getBookingRefundInfo from './getBookingRefundInfo.graphql';
import createRefundM from './createRefund.graphql';
import createDisputeM from './createDispute.graphql';
import updateDisputeStatusM from './updateDisputeStatus.graphql';

const RED = '#D32F2F';
const INK = '#1F2733';

class ManageRefundsDisputes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'refunds',
      busy: false,
      message: '',
      // refund form
      rfBooking: '', rfAmount: '', rfReason: '',
      // dispute form
      dpBooking: '', dpSubject: '', dpDescription: '', dpRaisedBy: 'customer',
      // pagination (each list is independent)
      refundsPage: 1, disputesPage: 1,
    };
  }

  info = () => (this.props.bookingInfo && this.props.bookingInfo.getBookingRefundInfo) || null;

  goRefunds = (delta) => this.setState((s) => ({ refundsPage: Math.max(1, s.refundsPage + delta) }), () => this.props.refundsData.refetch({ currentPage: this.state.refundsPage }));
  goDisputes = (delta) => this.setState((s) => ({ disputesPage: Math.max(1, s.disputesPage + delta) }), () => this.props.disputesData.refetch({ currentPage: this.state.disputesPage }));

  checkBooking = () => {
    const id = Number(this.state.rfBooking);
    if (!id) { this.setState({ message: 'Enter a booking ID first.' }); return; }
    this.setState({ message: '', rfAmount: '' });
    this.props.bookingInfo.refetch({ bookingId: id });
  };

  issueRefund = async () => {
    const info = this.info();
    if (!info || info.status !== 200 || !info.isRefundable) return;
    const amount = this.state.rfAmount ? Number(this.state.rfAmount) : info.refundable;
    if (!window.confirm(`Issue a REAL Stripe refund of ${amount} ${info.currency} on booking #${info.bookingId}? This moves real money back to the customer.`)) return;
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.createRefundMut({
        variables: { bookingId: info.bookingId, amount: this.state.rfAmount ? amount : undefined, reason: this.state.rfReason || undefined },
      });
      const r = res && res.data && res.data.createRefund;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: `✅ Refund of ${amount} ${info.currency} issued.`, rfAmount: '', rfReason: '' });
        this.props.refundsData.refetch();
        this.props.bookingInfo.refetch({ bookingId: info.bookingId });
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Refund failed.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  submitDispute = async () => {
    const bookingId = Number(this.state.dpBooking);
    if (!bookingId || !this.state.dpSubject) { this.setState({ message: 'Booking ID and subject are required.' }); return; }
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.createDisputeMut({
        variables: { bookingId, subject: this.state.dpSubject, description: this.state.dpDescription || undefined, raisedBy: this.state.dpRaisedBy },
      });
      const r = res && res.data && res.data.createDispute;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: '✅ Dispute logged.', dpBooking: '', dpSubject: '', dpDescription: '' });
        this.props.disputesData.refetch();
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Could not create dispute.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  setDisputeStatus = async (id, status) => {
    let resolution;
    if (status === 'resolved' || status === 'rejected') {
      resolution = window.prompt(`Resolution note for marking this dispute "${status}":`, '');
      if (resolution === null) return;
    }
    this.setState({ busy: true, message: '' });
    try {
      const res = await this.props.updateDisputeMut({ variables: { id, status, resolution } });
      const r = res && res.data && res.data.updateDisputeStatus;
      if (r && r.status === 200) {
        this.setState({ busy: false, message: `Dispute #${id} → ${status}.` });
        this.props.disputesData.refetch();
      } else {
        this.setState({ busy: false, message: (r && r.errorMessage) || 'Update failed.' });
      }
    } catch (e) { this.setState({ busy: false, message: e.message }); }
  };

  render() {
    const input = { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, marginRight: 8 };
    const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginRight: 8 });
    const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 16 };
    const tabBtn = (t) => ({ ...btn(this.state.tab === t ? RED : '#fff'), color: this.state.tab === t ? '#fff' : INK, border: this.state.tab === t ? 'none' : '1px solid #ddd' });
    const stBadge = (st) => {
      const c = { succeeded: '#2E7D32', resolved: '#2E7D32', pending: '#F9A825', open: '#F9A825', under_review: '#1565C0', failed: '#C62828', rejected: '#9E9E9E' }[st] || '#9E9E9E';
      return { padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, color: '#fff', background: c };
    };

    const refunds = (this.props.refundsData && this.props.refundsData.getAllRefunds && this.props.refundsData.getAllRefunds.results) || [];
    const disputes = (this.props.disputesData && this.props.disputesData.getAllDisputes && this.props.disputesData.getAllDisputes.results) || [];
    const openCount = (this.props.disputesData && this.props.disputesData.getAllDisputes && this.props.disputesData.getAllDisputes.openCount) || 0;
    const refundsCount = (this.props.refundsData && this.props.refundsData.getAllRefunds && this.props.refundsData.getAllRefunds.count) || 0;
    const disputesCount = (this.props.disputesData && this.props.disputesData.getAllDisputes && this.props.disputesData.getAllDisputes.count) || 0;
    const PAGE_SIZE = 10;
    const info = this.info();

    return (
      <div style={{ fontFamily: 'Poppins, sans-serif' }}>
        <h1 className="siteAdminPrimaryHeading">Refunds &amp; Disputes</h1>

        <div style={{ marginBottom: 16 }}>
          <button style={tabBtn('refunds')} onClick={() => this.setState({ tab: 'refunds', message: '' })}>Refunds</button>
          <button style={tabBtn('disputes')} onClick={() => this.setState({ tab: 'disputes', message: '' })}>Disputes {openCount ? `(${openCount} open)` : ''}</button>
          {this.state.message ? <span style={{ marginLeft: 12, color: RED, fontWeight: 600 }}>{this.state.message}</span> : null}
        </div>

        {this.state.tab === 'refunds' && (
          <div>
            {/* New refund */}
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Issue a refund</div>
              <input style={{ ...input, width: 130 }} placeholder="Booking ID" value={this.state.rfBooking}
                     onChange={(e) => this.setState({ rfBooking: e.target.value.replace(/[^0-9]/g, '') })} />
              <button style={btn(INK)} onClick={this.checkBooking}>Check</button>

              {info && info.status === 200 && (
                <div style={{ marginTop: 12, fontSize: 14 }}>
                  <div>Paid by customer: <b>{info.amountPaid} {info.currency}</b> · Already refunded: <b>{info.alreadyRefunded}</b> · Refundable: <b style={{ color: RED }}>{info.refundable} {info.currency}</b></div>
                  {info.isRefundable ? (
                    <div style={{ marginTop: 10 }}>
                      <input style={{ ...input, width: 130 }} placeholder={`Amount (max ${info.refundable})`} value={this.state.rfAmount}
                             onChange={(e) => this.setState({ rfAmount: e.target.value.replace(/[^0-9.]/g, '') })} />
                      <input style={{ ...input, width: 260 }} placeholder="Reason (optional)" value={this.state.rfReason}
                             onChange={(e) => this.setState({ rfReason: e.target.value })} />
                      <button style={btn(RED)} disabled={this.state.busy} onClick={this.issueRefund}>Issue refund</button>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Leave amount empty for a full refund of the remaining balance.</div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, color: '#C62828' }}>{info.note || 'Not refundable.'}</div>
                  )}
                </div>
              )}
              {this.state.rfBooking && info && info.status !== 200 && info.errorMessage && (
                <div style={{ marginTop: 10, color: '#C62828' }}>{info.errorMessage}</div>
              )}
            </div>

            {/* Refund history */}
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>#</th><th style={{ padding: 10 }}>Booking</th><th style={{ padding: 10 }}>Amount</th>
                  <th style={{ padding: 10 }}>Type</th><th style={{ padding: 10 }}>Status</th><th style={{ padding: 10 }}>Reason</th>
                  <th style={{ padding: 10 }}>By</th><th style={{ padding: 10 }}>Date</th>
                </tr></thead>
                <tbody>
                  {refunds.length === 0 && <tr><td colSpan={8} style={{ padding: 20 }}>No refunds yet.</td></tr>}
                  {refunds.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 10 }}>{r.id}</td>
                      <td style={{ padding: 10 }}>#{r.bookingId}</td>
                      <td style={{ padding: 10 }}>{r.amount} {r.currency}</td>
                      <td style={{ padding: 10 }}>{r.refundType}</td>
                      <td style={{ padding: 10 }}><span style={stBadge(r.status)}>{r.status}</span>{r.status === 'failed' && r.failureReason ? <div style={{ fontSize: 11, color: '#C62828' }}>{r.failureReason}</div> : null}</td>
                      <td style={{ padding: 10 }}>{r.reason || '—'}</td>
                      <td style={{ padding: 10 }}>{r.adminEmail || '—'}</td>
                      <td style={{ padding: 10 }}>{r.createdAt ? r.createdAt.slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {refundsCount > PAGE_SIZE && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button style={btn(RED)} disabled={this.state.refundsPage <= 1} onClick={() => this.goRefunds(-1)}>Prev</button>
                <span>Page {this.state.refundsPage} / {Math.max(1, Math.ceil(refundsCount / PAGE_SIZE))} ({refundsCount})</span>
                <button style={btn(RED)} disabled={this.state.refundsPage >= Math.ceil(refundsCount / PAGE_SIZE)} onClick={() => this.goRefunds(1)}>Next</button>
              </div>
            )}
          </div>
        )}

        {this.state.tab === 'disputes' && (
          <div>
            {/* New dispute */}
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Log a dispute</div>
              <input style={{ ...input, width: 110 }} placeholder="Booking ID" value={this.state.dpBooking}
                     onChange={(e) => this.setState({ dpBooking: e.target.value.replace(/[^0-9]/g, '') })} />
              <select style={input} value={this.state.dpRaisedBy} onChange={(e) => this.setState({ dpRaisedBy: e.target.value })}>
                <option value="customer">Customer</option>
                <option value="provider">Provider</option>
                <option value="admin">Admin</option>
              </select>
              <input style={{ ...input, width: 240 }} placeholder="Subject" value={this.state.dpSubject}
                     onChange={(e) => this.setState({ dpSubject: e.target.value })} />
              <input style={{ ...input, width: 320 }} placeholder="Description (optional)" value={this.state.dpDescription}
                     onChange={(e) => this.setState({ dpDescription: e.target.value })} />
              <button style={btn(RED)} disabled={this.state.busy} onClick={this.submitDispute}>Log dispute</button>
            </div>

            {/* Dispute queue */}
            <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                  <th style={{ padding: 10 }}>#</th><th style={{ padding: 10 }}>Booking</th><th style={{ padding: 10 }}>By</th>
                  <th style={{ padding: 10 }}>Subject</th><th style={{ padding: 10 }}>Status</th><th style={{ padding: 10 }}>Date</th><th style={{ padding: 10 }}>Actions</th>
                </tr></thead>
                <tbody>
                  {disputes.length === 0 && <tr><td colSpan={7} style={{ padding: 20 }}>No disputes.</td></tr>}
                  {disputes.map((d) => (
                    <tr key={d.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 10 }}>{d.id}</td>
                      <td style={{ padding: 10 }}>#{d.bookingId}</td>
                      <td style={{ padding: 10 }}>{d.raisedBy}</td>
                      <td style={{ padding: 10 }}>{d.subject}{d.description ? <div style={{ fontSize: 12, color: '#888' }}>{d.description}</div> : null}{d.resolution ? <div style={{ fontSize: 12, color: '#2E7D32' }}>→ {d.resolution}</div> : null}</td>
                      <td style={{ padding: 10 }}><span style={stBadge(d.status)}>{d.status}</span></td>
                      <td style={{ padding: 10 }}>{d.createdAt ? d.createdAt.slice(0, 10) : '—'}</td>
                      <td style={{ padding: 10, whiteSpace: 'nowrap' }}>
                        {d.status !== 'resolved' && d.status !== 'rejected' && (
                          <>
                            {d.status === 'open' && <button style={btn('#1565C0')} disabled={this.state.busy} onClick={() => this.setDisputeStatus(d.id, 'under_review')}>Review</button>}
                            <button style={btn('#2E7D32')} disabled={this.state.busy} onClick={() => this.setDisputeStatus(d.id, 'resolved')}>Resolve</button>
                            <button style={btn('#9E9E9E')} disabled={this.state.busy} onClick={() => this.setDisputeStatus(d.id, 'rejected')}>Reject</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {disputesCount > PAGE_SIZE && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button style={btn(RED)} disabled={this.state.disputesPage <= 1} onClick={() => this.goDisputes(-1)}>Prev</button>
                <span>Page {this.state.disputesPage} / {Math.max(1, Math.ceil(disputesCount / PAGE_SIZE))} ({disputesCount})</span>
                <button style={btn(RED)} disabled={this.state.disputesPage >= Math.ceil(disputesCount / PAGE_SIZE)} onClick={() => this.goDisputes(1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  withQuery(getAllRefunds, { name: 'refundsData', options: { variables: { currentPage: 1 }, fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getAllDisputes, { name: 'disputesData', options: { variables: { currentPage: 1 }, fetchPolicy: 'network-only', ssr: false } }),
  withQuery(getBookingRefundInfo, { name: 'bookingInfo', options: { variables: { bookingId: 0 }, fetchPolicy: 'network-only', ssr: false } }),
  withMutation(createRefundM, { name: 'createRefundMut' }),
  withMutation(createDisputeM, { name: 'createDisputeMut' }),
  withMutation(updateDisputeStatusM, { name: 'updateDisputeMut' }),
)(ManageRefundsDisputes);
